import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags } from '@fortawesome/free-solid-svg-icons';
import {
  createPricingTier,
  getPricingOptions,
  updatePricingTier,
} from '../../../lib/api';
import './admin.css';

const emptyForm = {
  marketing: '0.85',
  utility: '0.15',
  authentication: '0.15',
  service: '0.15',
};

const ManagePricing = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [editForm, setEditForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getPricingOptions({ limit: 100 });
      setRows(res?.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await createPricingTier({
        marketing: Number(form.marketing),
        utility: Number(form.utility),
        authentication: Number(form.authentication),
        service: Number(form.service),
      });
      setForm(emptyForm);
      setMessage('Pricing tier created. Assign it when creating a user.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(String(row.pricing_id));
    setEditForm({
      marketing: String(row.marketing),
      utility: String(row.utility),
      authentication: String(row.authentication),
      service: String(row.service ?? '0.15'),
    });
  };

  const onSaveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await updatePricingTier(editingId, {
        marketing: Number(editForm.marketing),
        utility: Number(editForm.utility),
        authentication: Number(editForm.authentication),
        service: Number(editForm.service),
      });
      setEditingId('');
      setMessage('Pricing tier updated. Bot messages use the Service rate.');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faTags} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Admin</span>
            <h1 className="admin-page__title">Pricing tiers</h1>
            <p className="admin-page__subtitle">
              Set per-tier rates. <strong>Service</strong> is charged for each Bot Builder
              outbound message. Assign a tier when creating a user.
            </p>
          </div>
        </div>
      </header>

      {error && <div className="admin-feedback admin-feedback--error">{error}</div>}
      {message && <div className="admin-feedback admin-feedback--success">{message}</div>}

      <section className="admin-panel" style={{ marginBottom: 16 }}>
        <div className="admin-panel__head">
          <h2>New tier</h2>
          <p>Service rate is used for each Bot Builder outbound WhatsApp message.</p>
        </div>
        <div className="admin-panel__body">
          <div className="admin-grid">
            {['marketing', 'utility', 'authentication', 'service'].map((key) => (
              <div className="admin-field" key={key}>
                <span className="admin-label">{key}</span>
                <input
                  className="admin-input"
                  type="number"
                  step="0.00001"
                  min="0"
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="admin-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={onCreate}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Create tier'}
            </button>
          </div>
        </div>
      </section>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>Existing tiers</h2>
          <p>Edit rates; users keep their assigned pricing_id.</p>
        </div>
        <div className="admin-panel__body">
          {loading ? (
            <p>Loading…</p>
          ) : (
            <div className="admin-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Marketing</th>
                    <th>Utility</th>
                    <th>Auth</th>
                    <th>Service (bot)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const editing = String(row.pricing_id) === editingId;
                    return (
                      <tr key={row.pricing_id}>
                        <td>{row.pricing_id}</td>
                        {editing ? (
                          <>
                            {['marketing', 'utility', 'authentication', 'service'].map((key) => (
                              <td key={key}>
                                <input
                                  className="admin-input"
                                  type="number"
                                  step="0.00001"
                                  min="0"
                                  value={editForm[key]}
                                  onChange={(e) =>
                                    setEditForm((f) => ({ ...f, [key]: e.target.value }))
                                  }
                                />
                              </td>
                            ))}
                            <td>
                              <button
                                type="button"
                                className="admin-btn admin-btn--primary admin-btn--sm"
                                onClick={onSaveEdit}
                                disabled={saving}
                              >
                                Save
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{row.marketing}</td>
                            <td>{row.utility}</td>
                            <td>{row.authentication}</td>
                            <td>{row.service ?? '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="admin-btn admin-btn--ghost admin-btn--sm"
                                onClick={() => startEdit(row)}
                              >
                                Edit
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ManagePricing;
